import * as React from 'react';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import { ServicePosition } from 'api/servicePosition';
import classnames from 'classnames';
import { formatDecimal } from 'utils/formatter';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { formStyles } from 'styles/formStyles';
import { FormDecimalField } from 'formComponents/FormDecimalField';

interface ServicePositionenProps {
    servicePositionen: ServicePosition[];
    onChange: (newTransferPositionen: ServicePosition[]) => void;
    withLArt: boolean;
}

export const ServicePositionenSelect: React.FC<ServicePositionenProps> = ({
    servicePositionen,
    onChange,
    withLArt,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    return (
        <div>
            <h3>Bitte wählen Sie die zu übernehmenden Positionen und Mengen</h3>
            <table className={formStyles.positionenTable}>
                <thead className={formStyles.positionenTable}>
                    <tr>
                        <td />
                        <td className={formStyles.positionenNrCell}>Pos.</td>
                        <td className={formStyles.positionenArtikelGruppeCell}>Art. Grp.</td>
                        <td className={formStyles.positionenArtikelNrCell}>Art. Nr.</td>
                        <td className={formStyles.positionenTextCell}>Text</td>
                        <td className={formStyles.positionenEinhCell}>Einh</td>
                        <td className={formStyles.positionenMoneyCell}>Menge</td>
                        <td className={formStyles.transferMengeHeader}>
                            Menge
                            <br />
                            übernehmen
                        </td>
                        {withLArt && <td className={formStyles.transferLArt}>LArt</td>}
                    </tr>
                </thead>
                <tbody>
                    {servicePositionen.map((servicePosition) => (
                        <tr key={servicePosition.pos} className={formStyles.positionenRow}>
                            <td>
                                <Checkbox
                                    className={formStyles.checkbox}
                                    checked={servicePosition.transfer}
                                    onChange={(event, checked) => {
                                        onChange(
                                            servicePositionen.map((ap) =>
                                                ap.pos === servicePosition.pos
                                                    ? {
                                                          ...servicePosition,
                                                          transfer: checked,
                                                      }
                                                    : ap,
                                            ),
                                        );
                                    }}
                                />
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                {servicePosition.pos}
                            </td>
                            <td
                                className={classnames(
                                    formStyles.positionenCell,
                                    formStyles.positionenArtikelGruppeCell,
                                )}
                            >
                                {(artikelGruppenByNr[servicePosition.artikelGruppeNr] &&
                                    artikelGruppenByNr[servicePosition.artikelGruppeNr]?.text) ||
                                    servicePosition.artikelGruppeNr}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenArtikelNrCell)}>
                                <span>{servicePosition.artikelNr}</span>
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}>
                                {servicePosition.posText}
                                {servicePosition.posBem ? (
                                    <i>
                                        <br />
                                        {servicePosition.posBem}
                                    </i>
                                ) : (
                                    ''
                                )}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}>
                                {servicePosition.posEinh
                                    ? einheitenByCode[servicePosition.posEinh].text || servicePosition.posEinh
                                    : '?'}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMengeCell)}>
                                {formatDecimal(servicePosition.posMenge, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                <FormDecimalField
                                    options={{
                                        disabled: !servicePosition.transfer,
                                        fixed: 3,
                                    }}
                                    label=""
                                    value={servicePosition.transferMenge || 0}
                                    onChange={(newValue): void => {
                                        onChange(
                                            servicePositionen.map((ap) =>
                                                ap.pos === servicePosition.pos
                                                    ? {
                                                          ...servicePosition,
                                                          transferMenge: newValue,
                                                      }
                                                    : ap,
                                            ),
                                        );
                                    }}
                                />
                            </td>
                            {withLArt && (
                                <td className={classnames(formStyles.positionenCell, formStyles.transferLArt)}>
                                    <LieferArt
                                        allowEmpty
                                        disabled={!servicePosition.transfer}
                                        value={(servicePosition?.transferLArt as string) || ''}
                                        menuPlacement="top"
                                        renderLabel="no"
                                        onChange={(newValue) => {
                                            onChange(
                                                servicePositionen.map((ap) =>
                                                    ap.pos === servicePosition.pos
                                                        ? {
                                                              ...servicePosition,
                                                              transferLArt: newValue,
                                                          }
                                                        : ap,
                                                ),
                                            );
                                        }}
                                    />
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
